/**
 * The faithful, documented catch algorithm (Gen III/IV maths — the same model the
 * @/shared/battle JSDoc names). Pure + deterministic: every roll flows through an
 * injected `Rng` (core/rng) so a capture is fully reproducible from its seed.
 *
 *   inner = floor((3*maxHP - 2*curHP) * captureRate * ballBonus / (3*maxHP))
 *   a     = floor(inner * statusBonus)
 *   if a >= 255            ⇒ guaranteed catch (4 shakes, no rolls consumed)
 *   else b = floor(1048560 / floor(sqrt(floor(sqrt(floor(16711680 / a))))))
 *           4 shake checks; each passes iff rng.int(65536) < b; the ball only holds
 *           if ALL four pass. The mon breaks free on the first failed shake.
 *
 * A failed catch is always "it broke free", never a punishment (CLAUDE.md / D6).
 */
import type { StatusCondition } from '@/shared';
import type { Rng } from './rng.js';

/** Known ball kinds and their catch-rate multipliers (Gen III/IV values). */
export type BallId = 'poke' | 'great' | 'ultra' | 'net' | 'dusk' | 'master';

/** Catch-rate multiplier per ball. Unknown balls fall back to a plain Poké Ball. */
const BALL_BONUS: Readonly<Record<BallId, number>> = {
  poke: 1,
  great: 1.5,
  ultra: 2,
  net: 3.5,
  dusk: 3,
  master: 255,
};

/**
 * The minimal shape the catch maths needs off a wild target: its current/max HP,
 * its non-volatile status, and the species capture rate. A `PokemonInstance`
 * carries the HP/status; the caller supplies the species `captureRate`.
 */
export interface CatchTarget {
  readonly maxHp: number;
  readonly currentHp: number;
  readonly status: StatusCondition;
  /** Species catch rate, 0–255 (higher = easier). */
  readonly captureRate: number;
}

/** The outcome of one ball throw. */
export interface CatchResult {
  readonly caught: boolean;
  /** Wobbles the ball made (0–3 on a break-free, 4 on a catch). */
  readonly shakes: number;
}

/**
 * Status multiplier for the catch formula (Gen III/IV): sleep & freeze ×2.0;
 * paralysis, poison, toxic, burn ×1.5; no status ×1.0.
 */
export function statusCatchBonus(status: StatusCondition): number {
  if (status === 'sleep' || status === 'freeze') return 2.0;
  if (status === 'paralysis' || status === 'poison' || status === 'toxic' || status === 'burn') {
    return 1.5;
  }
  return 1.0;
}

/** Catch-rate multiplier for a ball; unknown balls behave like a Poké Ball. */
export function ballCatchBonus(ball: BallId): number {
  return BALL_BONUS[ball] ?? 1;
}

/** The modified catch value `a`. `a >= 255` ⇒ guaranteed capture. */
export function computeCatchValueA(target: CatchTarget, ball: BallId): number {
  const { maxHp, currentHp } = target;
  const inner = Math.floor(
    ((3 * maxHp - 2 * currentHp) * target.captureRate * ballCatchBonus(ball)) / (3 * maxHp),
  );
  return Math.floor(inner * statusCatchBonus(target.status));
}

/** The shake-probability threshold `b` derived from `a` (Gen III/IV). */
export function computeShakeThresholdB(a: number): number {
  return Math.floor(
    1048560 / Math.floor(Math.sqrt(Math.floor(Math.sqrt(Math.floor(16711680 / a))))),
  );
}

/**
 * Throw a ball at a wild target. Pure: all randomness comes from `rng`. When
 * `a >= 255` the catch is guaranteed and no rolls are consumed; otherwise four
 * shake checks run and the mon is caught only if every one passes.
 */
export function attemptCatch(target: CatchTarget, ball: BallId, rng: Rng): CatchResult {
  const a = computeCatchValueA(target, ball);
  if (a >= 255) {
    return { caught: true, shakes: 4 };
  }

  const b = computeShakeThresholdB(a);
  let shakes = 0;
  for (let i = 0; i < 4; i++) {
    if (rng.int(65536) < b) {
      shakes++;
    } else {
      return { caught: false, shakes };
    }
  }
  return { caught: true, shakes: 4 };
}
